import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IChamado,
  IChamadoDetalhe,
  IChamadoFiltro,
  IResponse,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';

type ChamadoAdminFiltros = Pick<
  IChamadoFiltro,
  'codigo' | 'pessoaNome' | 'status'
>;

@Injectable({ providedIn: 'root' })
export class ChamadosAdminDataService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private baseUrl = `${this.env.getApiUrl('nomoh')}/chamado`;

  private _chamados = signal<IChamado[]>([]);
  private _detalhe = signal<IChamadoDetalhe | null>(null);
  private _aguardando = signal(0);
  private _loading = signal(false);
  private _filtros = signal<ChamadoAdminFiltros>({});

  $chamados = this._chamados.asReadonly();
  $detalhe = this._detalhe.asReadonly();
  $aguardando = this._aguardando.asReadonly();
  $loading = this._loading.asReadonly();
  $filtros = this._filtros.asReadonly();

  async carregarFila(filtros?: ChamadoAdminFiltros): Promise<void> {
    if (filtros !== undefined) {
      this._filtros.set(filtros);
    }

    this._loading.set(true);
    try {
      const ativos = this._filtros();
      const body: IChamadoFiltro = {
        offSet: 0,
        limit: 100,
        ordenacao: 1,
        param: 'dataInclusao',
      };

      if (ativos.codigo?.trim()) {
        body.codigo = ativos.codigo.trim();
      }
      if (ativos.pessoaNome?.trim()) {
        body.pessoaNome = ativos.pessoaNome.trim();
      }
      if (ativos.status) {
        body.status = ativos.status;
      }

      const response = await firstValueFrom(
        this.http.post<IResponse<IChamado[]> | IChamado[]>(
          `${this.baseUrl}/findAll`,
          body
        )
      );

      this._chamados.set(this.extrairLista(response));
    } finally {
      this._loading.set(false);
    }
  }

  limparFiltros(): Promise<void> {
    return this.carregarFila({});
  }

  private extrairLista(
    response: IResponse<IChamado[]> | IChamado[] | null | undefined
  ): IChamado[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    return response.data ?? [];
  }

  async carregarAguardando(): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<{ total: number }>(`${this.baseUrl}/countAguardando`, {})
    );
    this._aguardando.set(response.total ?? 0);
  }

  async carregarDetalhe(id: string): Promise<void> {
    this._loading.set(true);
    try {
      const detalhe = await firstValueFrom(
        this.http.post<IChamadoDetalhe>(`${this.baseUrl}/admin/findById`, { id })
      );
      this._detalhe.set(detalhe);
    } finally {
      this._loading.set(false);
    }
  }

  async responder(chamadoId: string, texto: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.baseUrl}/responder`, { chamadoId, texto })
    );
    await this.carregarDetalhe(chamadoId);
    await this.carregarAguardando();
  }

  async fechar(chamadoId: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.baseUrl}/fechar`, { chamadoId })
    );
    await this.carregarDetalhe(chamadoId);
    await this.carregarFila();
    await this.carregarAguardando();
  }
}
